import isEmpty from 'lodash-es/isEmpty';

import { FamilyGroup } from '@gn-audio/jss-core';
import { Option } from '@gn-audio/jss-core/common/models';

import { Filter } from 'api/jabraPublicApi';
import { getGroupAttributesObject } from 'composables/Product/Buy/customizerHelpers';

export enum CustomizerFilterTypes {
  RadioButtons = 'RadioButtons',
  Checkbox = 'Checkbox',
  ColorPicker = 'ColorPicker',
}

const COLOR_FILTER_NAME = 'product_mobcolorhex';

export enum FilterOperatorType {
  include,
  exclude,
}

export type FilterCondition = {
  operator: FilterOperatorType;
  option: Option;
};

export const getApplicableFilters = (groups: FamilyGroup[], allFilters: Filter[]): Filter[] => {
  const applicableFilters = allFilters?.map((filter) => {
    const groupsWithFilterableAttribute = groups.filter((group) =>
      group.attributes.some((attr) => attr.key == filter.name)
    );
    const availableOptions = filter.options.filter((option) => {
      const attributeVariations = groupsWithFilterableAttribute.flatMap(
        (group) => group.attributes
      );

      return attributeVariations.some((attr) => {
        return attr.valueKey == option.value && attr.key == option.key;
      });
    });
    return { ...filter, options: availableOptions };
  });

  return applicableFilters?.filter((filter) => filter.options.length > 0);
};

export const getFilterType = (filter: Filter): CustomizerFilterTypes | undefined => {
  if (filter.name.toLowerCase() === COLOR_FILTER_NAME) {
    return CustomizerFilterTypes.ColorPicker;
  }
  if (filter.options.length > 1) {
    return CustomizerFilterTypes.RadioButtons;
  }
  if (filter.options.length == 1) {
    return CustomizerFilterTypes.Checkbox;
  }
  return;
};

export const filterGroups = (
  groups: FamilyGroup[],
  selectedFilter: Record<string, FilterCondition>
): FamilyGroup[] => {
  return groups.filter((groupItem) => {
    return Object.entries(selectedFilter).every(([filterName, condition]) => {
      if (isEmpty(condition) || isEmpty(condition.option)) return true;

      const { option, operator } = condition;
      const filterableAttributes = groupItem.attributes.filter((attr) => attr.key === filterName);

      const include = filterableAttributes.some((attr) => attr.valueKey === option.value);

      return operator === FilterOperatorType.include ? include : !include;
    });
  });
};
export const buildSelectedFiltersBasedOnGroup = (
  applicableFilters: Filter[],
  group?: FamilyGroup
): Record<string, FilterCondition> => {
  const groupSpecs = getGroupAttributesObject(group);

  return applicableFilters.reduce((acc, filter) => {
    const values = groupSpecs[filter.name];
    const filterType = getFilterType(filter);
    if (filterType === CustomizerFilterTypes.Checkbox) {
      const [option] = filter.options;
      const includes = values && values.includes(option.value);
      return {
        ...acc,
        [filter.name]: {
          option,
          operator: includes ? FilterOperatorType.include : FilterOperatorType.exclude,
        },
      };
    }

    const selectedOption = filter.options.find((opt) => values?.includes(opt.value));
    if (!selectedOption) return acc;
    return {
      ...acc,
      [filter.name]: { option: selectedOption, operator: FilterOperatorType.include },
    };
  }, {});
};
